import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { CONTACT } from "@/data/projects";
import { EASE } from "@/components/motion";

const LINKS = [
  { label: "About", to: "/about" },
  { label: "Expertise", to: "/expertise" },
  { label: "Work", to: "/work" },
  { label: "Experience", to: "/experience" },
  { label: "Contact", to: "/contact" },
];

export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70, duration: 1.3 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const isActive = (to) =>
    location.pathname === to || (to === "/work" && location.pathname.startsWith("/work/"));

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
        className={`fixed inset-x-0 top-0 z-50 border-b border-hairline bg-paper/80 backdrop-blur-xl transition-[padding] duration-500 ${
          scrolled ? "py-3" : "py-5"
        }`}
        data-testid="main-nav"
      >
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 sm:px-10">
          <Link
            to="/"
            data-testid="nav-logo"
            className={`font-serif font-medium tracking-tight text-ink transition-[font-size] duration-500 ${
              scrolled ? "text-xl" : "text-2xl"
            }`}
          >
            RP<span className="text-champagne">.</span>
          </Link>

          <nav className="hidden items-center gap-8 md:flex" aria-label="Primary">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                aria-current={isActive(l.to) ? "page" : undefined}
                className={`group relative text-[13px] font-medium uppercase tracking-[0.16em] transition-colors duration-300 ${
                  isActive(l.to) ? "text-ink" : "text-smoke hover:text-ink"
                }`}
              >
                {l.label}
                <span
                  className={`absolute -bottom-1 left-0 h-px bg-moss transition-[width] duration-300 ${
                    isActive(l.to) ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                  aria-hidden="true"
                />
              </Link>
            ))}
            <a
              href={CONTACT.cv}
              download
              data-testid="nav-download-cv"
              className="group ml-2 inline-flex items-center gap-1.5 border border-ink px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.16em] text-ink transition-colors duration-300 hover:bg-ink hover:text-paper"
            >
              Download CV
              <ArrowUpRight size={13} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </nav>

          <button
            className="md:hidden text-ink"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="nav-menu-open"
          >
            <Menu size={22} strokeWidth={1.5} />
          </button>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[60] flex flex-col bg-moss px-8 py-8"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between">
              <span className="font-serif text-2xl text-paper">RP<span className="text-champagne">.</span></span>
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="nav-menu-close" className="text-paper">
                <X size={24} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="mt-16 flex flex-col gap-2" aria-label="Mobile">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.6, ease: EASE }}
                >
                  <Link
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    aria-current={isActive(l.to) ? "page" : undefined}
                    className={`block border-b border-paper/10 py-4 text-left font-serif text-4xl font-light ${
                      isActive(l.to) ? "text-champagne" : "text-paper"
                    }`}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <motion.a
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.6, ease: EASE }}
                href={CONTACT.cv}
                download
                data-testid="mobile-nav-download-cv"
                className="mt-8 inline-flex w-fit items-center gap-2 border border-champagne px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.18em] text-champagne"
              >
                Download CV <ArrowUpRight size={14} strokeWidth={1.5} />
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
